import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Pencil, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  usePortalRoles,
  usePortalModules,
  useUpdatePortalRoleModules,
} from '@/hooks/react-query/usePortalRoles';
import { queryKeys } from '@/hooks/react-query/queryKeys';
import type { PortalRole, PortalModule } from '@/services/portalRoleService';
import { CreatePortalRoleDialog } from './CreatePortalRoleDialog';
import { EditPortalRoleDialog } from './EditPortalRoleDialog';
import { DeletePortalRoleDialog } from './DeletePortalRoleDialog';

// Color palette for roles
const ROLE_COLORS: Record<string, string> = {
  full_access: '#22c55e',
  investment_officer: '#3b82f6',
  family_office_manager: '#8b5cf6',
  operational_support: '#f59e0b',
  accountant: '#06b6d4',
};

const getRoleColor = (roleName: string): string => {
  return ROLE_COLORS[roleName] || '#6b7280';
};

type RolesCache = { items: PortalRole[]; total: number };

export function PortalRolesPermissions() {
  const [savingModules, setSavingModules] = useState<Set<string>>(
    () => new Set()
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<PortalRole | null>(null);

  const queryClient = useQueryClient();
  const rolesQueryKey = queryKeys.portalRoles.list();
  const {
    data: rolesData,
    isLoading: rolesLoading,
    refetch: refetchRoles,
  } = usePortalRoles();
  const { data: modulesData, isLoading: modulesLoading } = usePortalModules();
  const updateRoleModulesMutation = useUpdatePortalRoleModules();

  const modules = modulesData?.items || [];
  const roles = rolesData?.items || [];

  const hasModuleAccess = (role: PortalRole, module: PortalModule): boolean =>
    role.modules?.some((m) => m.id === module.id) || false;

  const applyToggle = (role: PortalRole, module: PortalModule, add: boolean) =>
    queryClient.setQueryData<RolesCache>(rolesQueryKey, (cache) => {
      if (!cache) return cache;
      return {
        ...cache,
        items: cache.items.map((r) => {
          if (r.id !== role.id) return r;
          const current = r.modules || [];
          const next = add
            ? [
                ...current,
                {
                  id: module.id,
                  name: module.name,
                  display_name: module.display_name,
                },
              ]
            : current.filter((m) => m.id !== module.id);
          return { ...r, modules: next };
        }),
      };
    });

  const toggleModuleAccess = async (role: PortalRole, module: PortalModule) => {
    const currentHasAccess = hasModuleAccess(role, module);
    const savingKey = `${role.id}-${module.id}`;
    const currentModuleIds = role.modules?.map((m) => m.id) || [];
    const newModuleIds = currentHasAccess
      ? currentModuleIds.filter((id) => id !== module.id)
      : [...currentModuleIds, module.id];

    applyToggle(role, module, !currentHasAccess);
    setSavingModules((prev) => new Set([...prev, savingKey]));

    try {
      await updateRoleModulesMutation.mutateAsync({
        roleId: role.id,
        moduleIds: newModuleIds,
      });
    } catch {
      applyToggle(role, module, currentHasAccess);
    } finally {
      setSavingModules((prev) => {
        const newSet = new Set(prev);
        newSet.delete(savingKey);
        return newSet;
      });
    }
  };

  const handleEditRole = (role: PortalRole) => {
    setSelectedRole(role);
    setEditDialogOpen(true);
  };

  const handleDeleteRole = (role: PortalRole) => {
    setSelectedRole(role);
    setDeleteDialogOpen(true);
  };

  const handleRoleChanged = () => {
    refetchRoles();
  };

  const isLoading = rolesLoading || modulesLoading;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="h-16 bg-gray-100 dark:bg-gray-800 rounded-lg"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            Portal Roles & Permissions
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {roles.length} role{roles.length !== 1 ? 's' : ''} configured
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Role
        </Button>
      </div>

      {/* Roles List */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
        {roles.map((role) => {
          const moduleCount = role.modules?.length || 0;
          const totalModules = modules.length;

          return (
            <div key={role.id} className="p-4">
              {/* Role Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-3">
                  <div
                    className="w-3 h-3 rounded-full shrink-0 mt-1.5"
                    style={{ backgroundColor: getRoleColor(role.name) }}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {role.display_name}
                      </span>
                      {role.is_default && (
                        <Badge variant="outline" className="text-xs">
                          Default
                        </Badge>
                      )}
                      <span className="text-sm text-gray-400 dark:text-gray-500">
                        {moduleCount}/{totalModules} modules
                      </span>
                    </div>
                    {role.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {role.description}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditRole(role)}
                    title="Edit role"
                  >
                    <Pencil className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRole(role)}
                    title="Delete role"
                    className="hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Module Checkboxes */}
              <div className="ml-6 flex flex-wrap gap-x-6 gap-y-2">
                {modules.map((module) => {
                  const hasAccess = hasModuleAccess(role, module);
                  const savingKey = `${role.id}-${module.id}`;
                  const isSaving = savingModules.has(savingKey);

                  return (
                    <label
                      key={module.id}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 -mx-1"
                      title={`Toggle ${module.display_name} access`}
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400 dark:text-gray-500" />
                      ) : (
                        <Checkbox
                          checked={hasAccess}
                          onCheckedChange={() =>
                            toggleModuleAccess(role, module)
                          }
                        />
                      )}
                      <span
                        className={
                          hasAccess
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-500 dark:text-gray-400'
                        }
                      >
                        {module.display_name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Role Dialog */}
      <CreatePortalRoleDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onRoleCreated={handleRoleChanged}
      />

      {/* Edit Role Dialog. Keyed on the role so it remounts with that role's
          values -- its form state is seeded from props at mount, so without
          this it would keep the state it had when it first mounted (role=null)
          and saving would blank the role out. */}
      <EditPortalRoleDialog
        key={selectedRole?.id}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        role={selectedRole}
        onRoleUpdated={handleRoleChanged}
      />

      {/* Delete Role Dialog */}
      <DeletePortalRoleDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        role={selectedRole}
        onRoleDeleted={handleRoleChanged}
      />
    </div>
  );
}
